/**
 * InlineUserExpansion - Expandable row showing all connected user data
 * Shows: profile, orgs, RSVPs, events, experiences, preference cards, defaults
 */
import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DefaultAvatar } from "@/components/DefaultAvatars";
import {
  Building2,
  Calendar,
  Scissors,
  Sparkles,
  Heart,
  Utensils,
  Shield,
  ChevronRight,
  Loader2,
  Share,
  Star,
  MapPin,
  Mail,
  Phone,
  Tag,
  Globe,
  Eye,
  ExternalLink,
  Clock,
  LayoutDashboard,
  UserCircle
} from "lucide-react";
import { getCategoryPreset } from "../../../lib/categoryConfig";
import HostAccessControlCenter from "./HostAccessControlCenter";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

async function openViewAsUser(userId, target = "/me") {
  try {
    const res = await fetch(`${API}/admin/view-as`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, target }),
    });
    const data = await res.json();
    const tok = data?.view_as_token || data?.token;
    let resolvedTarget = target;
    // Resolve "host" target to the user's primary_event_id
    if (target === "/host/dashboard" || target === "host") {
      try {
        const u = await fetch(`${API}/admin/database/users/record/${userId}`, { credentials: "include" });
        const ud = await u.json();
        const evt = ud?.record?.primary_event_id;
        if (evt) resolvedTarget = `/host/${evt}`;
      } catch {}
    }
    if (tok) {
      window.open(`${BACKEND}/auth/view-as?token=${tok}&target=${encodeURIComponent(resolvedTarget)}`, "_blank");
    } else if (data?.magic_link) {
      window.open(`${BACKEND}${data.magic_link}`, "_blank");
    } else {
      alert("Unable to generate view-as link. Check console.");
      console.error("view-as response:", data);
    }
  } catch (e) {
    console.error("view-as failed", e);
    alert("View-as failed: " + e.message);
  }
}

const SECTION_COLORS = {
  orgs: "border-blue-200 bg-blue-50/30",
  rsvps: "border-purple-200 bg-purple-50/30",
  experiences: "border-pink-200 bg-pink-50/30",
  defaults: "border-green-200 bg-green-50/30",
  intakes: "border-amber-200 bg-amber-50/30",
  shares: "border-indigo-200 bg-indigo-50/30",
};

export default function InlineUserExpansion({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/profiles/${userId}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
      }
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  if (loading) {
    return (
      <tr>
        <td colSpan={20} className="p-6 bg-gray-50/50">
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading profile...
          </div>
        </td>
      </tr>
    );
  }

  if (!data) return null;

  const { profile, defaults, rsvps = [], experiences = [], organizations = [], pilot_intakes = [], share_cards = [], inner_circle_memberships = [], linked_events = [] } = data;

  // Count filled defaults
  const filledDefaults = defaults ? Object.entries(defaults).filter(([k, v]) => k !== "raw_doc" && v && typeof v === "object" && Object.keys(v).length > 0) : [];

  return (
    <tr data-testid={`user-expansion-${userId}`}>
      <td colSpan={20} className="p-0">
        <div className="bg-gray-50/80 border-t border-b border-gray-200 p-4">
          {/* Profile Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {profile?.picture || profile?.profile_image ? (
                <Avatar className="h-12 w-12 ring-2 ring-white shadow-md">
                  <AvatarImage src={profile.picture || profile.profile_image} />
                  <AvatarFallback>
                    <DefaultAvatar
                      firstName={(profile?.name || "?").split(" ")[0]}
                      lastName={(profile?.name || "").split(" ").slice(1).join(" ")}
                      size={48}
                    />
                  </AvatarFallback>
                </Avatar>
              ) : (
                <DefaultAvatar
                  firstName={(profile?.name || "?").split(" ")[0]}
                  lastName={(profile?.name || "").split(" ").slice(1).join(" ")}
                  size={48}
                  className="ring-2 ring-white shadow-md"
                />
              )}
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  {profile?.name || "Unknown"}
                  {profile?.is_super_admin && <Shield className="w-4 h-4 text-purple-500" />}
                </h3>
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                  {profile?.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{profile.email}</span>}
                  {profile?.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{profile.phone}</span>}
                  {(profile?.city || profile?.location) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{profile.city || profile.location}</span>}
                </div>
                {/* Tags */}
                {profile?.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {profile.tags.map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] h-5">
                        {tag.replace("pilot_", "").replace("role_", "")}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openViewAsUser(userId, "/me")}
                className="text-xs px-2.5 py-1 rounded-full bg-black text-white hover:bg-gray-800 flex items-center gap-1"
                data-testid={`open-me-${userId}`}
              >
                <UserCircle className="w-3 h-3" /> Open /me
              </button>
              <button
                onClick={() => openViewAsUser(userId, "/host/dashboard")}
                className="text-xs px-2.5 py-1 rounded-full bg-white text-black border border-black hover:bg-gray-100 flex items-center gap-1"
                data-testid={`open-host-${userId}`}
              >
                <LayoutDashboard className="w-3 h-3" /> Open Host
              </button>
              <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-white">
                Collapse
              </button>
            </div>
          </div>

          {/* Host Access Control Center (one-stop pilot/dashboard control) */}
          {(profile?.role === "host" || profile?.is_host || profile?.is_pilot) && (
            <HostAccessControlCenter
              user={profile}
              organizations={organizations}
              linkedEvents={linked_events}
              pilotIntakes={pilot_intakes}
              onRefresh={() => { setData(null); fetchProfile(); }}
            />
          )}

          {/* Connected Data Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {/* Organizations */}
            <div className={`rounded-xl border p-3 ${SECTION_COLORS.orgs}`} data-testid="user-orgs-section">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                <Building2 className="w-3.5 h-3.5 text-blue-500" />
                Organizations
                <Badge variant="secondary" className="text-[10px] ml-auto">{organizations.length}</Badge>
              </h4>
              {organizations.length > 0 ? (
                <div className="space-y-1.5">
                  {organizations.map((org, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5 text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate font-medium">{org.name}</span>
                      </div>
                      <Badge variant="outline" className="text-[9px] shrink-0">{org.user_role || "member"}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No organizations</p>
              )}
            </div>

            {/* RSVPs */}
            <div className={`rounded-xl border p-3 ${SECTION_COLORS.rsvps}`} data-testid="user-rsvps-section">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                <Calendar className="w-3.5 h-3.5 text-purple-500" />
                RSVPs & Events
                <Badge variant="secondary" className="text-[10px] ml-auto">{rsvps.length}</Badge>
              </h4>
              {rsvps.length > 0 ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {rsvps.map((rsvp, i) => {
                    const catPreset = getCategoryPreset(rsvp.event_type || rsvp.pilot_type);
                    return (
                      <div key={i} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: catPreset?.iconColor || "#9ca3af" }} />
                          <div className="min-w-0">
                            <span className="truncate font-medium block">{rsvp.event_name || rsvp.event_id}</span>
                            {rsvp.event_date && <span className="text-[10px] text-gray-400">{new Date(rsvp.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                          </div>
                        </div>
                        <Badge variant={rsvp.status === "confirmed" ? "default" : "secondary"} className="text-[9px] shrink-0">
                          {rsvp.status || "rsvp"}
                        </Badge>
                        {/* Iter 9.37 - eye icon opens RSVP envelope */}
                        {(rsvp.invite_token || rsvp.event_slug || rsvp.event_id) && (
                          <button
                            onClick={() => {
                              const url = rsvp.invite_token
                                ? `/rsvp/${rsvp.invite_token}`
                                : rsvp.event_slug
                                  ? `/e/${rsvp.event_slug}`
                                  : `/e/${rsvp.event_id}`;
                              window.open(url, "_blank");
                            }}
                            className="text-purple-400 hover:text-purple-700 ml-1"
                            title="Open envelope"
                            data-testid={`open-rsvp-${rsvp.rsvp_id || rsvp.event_id}`}
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No RSVPs</p>
              )}
              {/* Linked Events (hosting, attending) */}
              {linked_events.length > 0 && (
                <div className="mt-2 pt-2 border-t border-purple-100">
                  <p className="text-[10px] font-semibold text-gray-500 mb-1">Linked Events ({linked_events.length})</p>
                  <div className="space-y-1">
                    {linked_events.slice(0, 5).map((evt, i) => (
                      <div key={i} className="flex items-center justify-between bg-white rounded-lg px-2 py-1 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <Calendar className="w-3 h-3 text-purple-400 shrink-0" />
                          <span className="truncate font-medium">{evt.name || evt.event_id}</span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Badge variant="outline" className="text-[9px] capitalize">{evt.relationship}</Badge>
                          {evt.pilot_category && <Badge variant="secondary" className="text-[9px] capitalize">{evt.pilot_category}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Experiences */}
            <div className={`rounded-xl border p-3 ${SECTION_COLORS.experiences}`} data-testid="user-experiences-section">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                <Heart className="w-3.5 h-3.5 text-pink-500" />
                Experiences
                <Badge variant="secondary" className="text-[10px] ml-auto">{experiences.length}</Badge>
              </h4>
              {experiences.length > 0 ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {experiences.map((exp, i) => {
                    const catPreset = getCategoryPreset(exp.category);
                    return (
                      <div key={i} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: catPreset?.iconColor || "#9ca3af" }} />
                          <span className="truncate font-medium">{exp.name || exp.title || exp.category}</span>
                        </div>
                        <span className="text-[9px] text-gray-400 shrink-0">{exp.type || "experience"}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No experiences</p>
              )}
            </div>

            {/* Defaults Summary */}
            <div className={`rounded-xl border p-3 ${SECTION_COLORS.defaults}`} data-testid="user-defaults-section">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                <Star className="w-3.5 h-3.5 text-green-500" />
                Saved Preferences
                <Badge variant="secondary" className="text-[10px] ml-auto">{filledDefaults.length}</Badge>
              </h4>
              {filledDefaults.length > 0 ? (
                <div className="space-y-2">
                  {filledDefaults.map(([key, val]) => {
                    const catPreset = getCategoryPreset(key);
                    const color = catPreset?.iconColor || "#9ca3af";
                    // Extract meaningful detail from the value
                    let details = [];
                    if (typeof val === "object" && val !== null) {
                      if (Array.isArray(val)) {
                        details = val.map(v => typeof v === "string" ? v : (v.name || v.label || JSON.stringify(v)));
                      } else {
                        Object.entries(val).forEach(([k, v]) => {
                          if (!v || k === "_id") return;
                          if (Array.isArray(v) && v.length > 0) {
                            details.push(...v.map(item => typeof item === "string" ? item : (item.name || item.label || "")));
                          } else if (typeof v === "string" && v.trim()) {
                            details.push(v);
                          }
                        });
                      }
                    }
                    details = details.filter(Boolean);
                    return (
                      <div key={key} className="bg-white rounded-lg px-2.5 py-2 text-xs border">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                          <span className="font-semibold capitalize text-gray-700">{key}</span>
                          <span className="text-[9px] text-gray-400">({details.length || Object.keys(val || {}).length})</span>
                        </div>
                        {details.length > 0 && (
                          <div className="flex flex-wrap gap-1 ml-3.5">
                            {details.slice(0, 8).map((d, i) => (
                              <span key={i} className="bg-gray-100 text-gray-600 rounded px-1.5 py-0.5 text-[10px]">{d}</span>
                            ))}
                            {details.length > 8 && <span className="text-[9px] text-gray-400">+{details.length - 8} more</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No preferences saved</p>
              )}
            </div>

            {/* Pilot Intakes */}
            <div className={`rounded-xl border p-3 ${SECTION_COLORS.intakes}`} data-testid="user-intakes-section">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                <Tag className="w-3.5 h-3.5 text-amber-500" />
                Pilot Intakes
                <Badge variant="secondary" className="text-[10px] ml-auto">{pilot_intakes.length}</Badge>
              </h4>
              {pilot_intakes.length > 0 ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {pilot_intakes.map((intake, i) => {
                    const catPreset = getCategoryPreset(intake.category || intake.pilot_type);
                    return (
                      <div key={i} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: catPreset?.iconColor || "#9ca3af" }} />
                          <span className="truncate font-medium">{intake.portal_name || intake.sub_category_name || intake.category || "Intake"}</span>
                        </div>
                        <Badge variant={intake.intake_complete ? "default" : "secondary"} className="text-[9px] shrink-0">
                          {intake.status || (intake.intake_complete ? "complete" : "pending")}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No intakes</p>
              )}
            </div>

            {/* Preference Card Shares */}
            <div className={`rounded-xl border p-3 ${SECTION_COLORS.shares}`} data-testid="user-shares-section">
              <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                <Share className="w-3.5 h-3.5 text-indigo-500" />
                Preference Cards
                <Badge variant="secondary" className="text-[10px] ml-auto">{(share_cards || []).length}</Badge>
              </h4>
              {(share_cards || []).length > 0 ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {share_cards.map((card, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5 text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <Share className="w-3 h-3 text-indigo-400 shrink-0" />
                        <span className="truncate font-medium">{card.share_id || card.category || "Card"}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {card.views > 0 && <span className="text-[9px] text-gray-400 flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{card.views}</span>}
                        {card.created_at && <span className="text-[9px] text-gray-400">{new Date(card.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                        {/* Iter 9.37 - eye icon opens full preference card view */}
                        <button
                          onClick={() => {
                            const url = card.share_url || `/c/${card.share_id || card.card_id}`;
                            window.open(url, "_blank");
                          }}
                          className="text-indigo-400 hover:text-indigo-700"
                          title="Open full preference card"
                          data-testid={`open-card-${card.share_id || card.card_id || i}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">No preference cards shared</p>
              )}
            </div>
          </div>

          {/* Beehiiv & Inner Circle Row */}
          {(data.beehiiv || inner_circle_memberships.length > 0) && (
            <div className="mt-3 flex items-center gap-3 text-xs">
              {data.beehiiv && (
                <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1">
                  <Globe className="w-3 h-3 text-orange-500" />
                  <span className="text-orange-700">Beehiiv: {data.beehiiv.status}</span>
                </div>
              )}
              {inner_circle_memberships.length > 0 && (
                <div className="flex items-center gap-1.5 bg-pink-50 border border-pink-200 rounded-lg px-2.5 py-1">
                  <Heart className="w-3 h-3 text-pink-500" />
                  <span className="text-pink-700">{inner_circle_memberships.length} Inner Circle{inner_circle_memberships.length !== 1 ? "s" : ""}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}
